import React from "react";
import { useTranslation } from "react-i18next";
import { Announcement, Close } from "@/components/icons";
import Modal from "@/components/ui/modal";
import Chip from "@/components/ui/chip";
import FileImage from "@/components/ui/file-image";
import type { ClientAnnouncement } from "../../types/client-announcements.types";
import styles from "./AnnouncementViewModal.module.scss";

export interface AnnouncementViewModalProps {
  announcement: ClientAnnouncement | null;
  isOpen: boolean;
  onClose: () => void;
}

const CATEGORY_CHIP_TYPE: Record<
  ClientAnnouncement["categoryType"],
  "success" | "primary" | "info" | "default"
> = {
  education: "success",
  system: "primary",
};

const AnnouncementViewModal: React.FC<AnnouncementViewModalProps> = ({
  announcement,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation();

  if (!announcement) return null;

  const chipType = CATEGORY_CHIP_TYPE[announcement.categoryType];

  const customHeader = (
    <div className={styles.header}>
      <div className={styles.headerLeft}>
        <Announcement width={24} height={24} className={styles.headerIcon} />
        <h3 className={styles.headerTitle}>
          {t("clientAnnouncements.modal.title")}
        </h3>
      </div>
      <div className={styles.headerActions}>
        <Chip type={chipType} variant="soft" size="small">
          {announcement.category}
        </Chip>
        <button
          type="button"
          className={styles.headerCloseBtn}
          onClick={onClose}
          aria-label={t("common.close")}
        >
          <Close width={24} height={24} color="#505766" border={false} />
        </button>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      customHeader={customHeader}
      customFooter={null}
      size="lg"
    >
      <div className={styles.body}>
        {(announcement.imageFileID || announcement.imageUrl) && (
          <div className={styles.imageWrap}>
            {announcement.imageFileID ? (
              <FileImage
                fileId={announcement.imageFileID}
                alt=""
                className={styles.image}
                objectFit="contain"
              />
            ) : (
              <img src={announcement.imageUrl!} alt="" className={styles.image} />
            )}
          </div>
        )}
        <div className={styles.content}>
          <h2 className={styles.contentTitle}>{announcement.title}</h2>
          <p className={styles.contentDescription}>
            {announcement.description}
          </p>
        </div>
      </div>
    </Modal>
  );
};

export default AnnouncementViewModal;
